// SolyticsDropdownOptions.jsx
// @ts-nocheck
/* eslint-disable react/prop-types */
import React from 'react';
import SolyticsBox from './SolyticsBox';
import SolyticsTypography from './SolyticsTypography';
import SolyticsCheckbox from './SolyticsCheckbox';

// KEY DESIGN TOKENS
const OPTION_BORDER_RADIUS = 0.5;

/**
 * SolyticsDropdownOptions - A single option row inside SolyticsDropdownPanel
 *
 * @component
 * @description
 * SolyticsDropdownOptions renders a single selectable option with optional checkbox and icon.
 * Supports keyboard navigation (Enter/Space) and visual selected/hover states.
 *
 * @param {Object} option - Option object: { label: string, value: *, icon?: ReactNode }
 * @param {boolean} [selected=false] - If true, marks the option as selected (aria-selected + checkbox checked)
 * @param {boolean} [indeterminate=false] - If true, renders checkbox in indeterminate state (for Select All)
 * @param {boolean} [showCheckbox=false] - If true, renders a checkbox before the label
 * @param {boolean} [showIcons=false] - If true, renders the option's icon before the label
 * @param {function} onSelect - Click/keydown handler called when the option is activated
 *
 * @returns {React.ReactElement} A single option row element
 */
const SolyticsDropdownOptions = ({
  option,
  selected,
  indeterminate,
  showCheckbox,
  showIcons,
  onSelect,
}) => {
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect();
    }
  };

  return (
    <SolyticsBox
      role="option"
      aria-selected={selected}
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        mx: 0.5,
        py: 1.5,
        px: 2,
        cursor: 'pointer',
        borderRadius: OPTION_BORDER_RADIUS,
        '&:hover': { background: (theme) => theme.palette.grey[30] },
        '&:focus-visible': {
          outline: (theme) => `2px solid ${theme.palette.lavenderBlue[80]}`,
          outlineOffset: '2px',
        },
      }}
    >
      {showCheckbox && (
        <SolyticsCheckbox
          name={'dropdown-checkbox'}
          checked={selected}
          indeterminate={indeterminate}
        />
      )}

      {showIcons && option?.icon && (
        <SolyticsBox sx={{ flexShrink: 0 }}>{option?.icon}</SolyticsBox>
      )}

      <SolyticsTypography color={(theme) => theme.palette.text.primary}>
        {option?.label}
      </SolyticsTypography>
    </SolyticsBox>
  );
};

export default SolyticsDropdownOptions;

/**
 * ## Features
 * - Click and keyboard (Enter/Space) activation
 * - Checkbox support with indeterminate state for "Select All"
 * - Optional icon rendering from option object
 * - Hover highlight and focus-visible outline for accessibility
 * - WCAG compliant with role="option", aria-selected, and tabIndex
 *
 * ## Examples
 *
 * ```jsx
 * <SolyticsDropdownOptions
 *   option={{ label: 'Active', value: 'active' }}
 *   selected={true}
 *   showCheckbox
 *   onSelect={() => handleSelect('active')}
 * />
 * ```
 */
